import { useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import { useLazyGetDataQuery, commonApi } from "../services/commonApi";

const shouldRetryDependentRequest = (error) => {
  const status = error?.status ?? error?.originalStatus ?? error?.error?.status;

  if (typeof status === "number") {
    return status >= 500;
  }

  return status === "FETCH_ERROR" || status === "TIMEOUT_ERROR";
};

export const useDependentData = (url, params = {}, options = {}) => {
  const { skip = false } = options;
  const stableArg = useMemo(() => ({
    url,
    data: params
  }), [url, JSON.stringify(params)]);

  const cached = useSelector((state) =>
    commonApi.endpoints.getData.select(stableArg)(state)
  );

  const [trigger, result] = useLazyGetDataQuery();

  const data = cached?.data?.data ?? [];

  const fetchData = useCallback(async (retries = 3) => {
    if (skip || cached?.data) return;

    try {
      const response = await trigger(stableArg).unwrap();
      return response;
    } catch (err) {
      if (retries > 0 && shouldRetryDependentRequest(err)) {
        return fetchData(retries - 1);
      }
      throw err;
    }
  }, [skip, cached?.data, trigger, stableArg, url]);

  return {
    data,
    fetchData,
    isLoading: result.isLoading,
    isError: result.isError,
    isCached: !!cached?.data,
  };
};
